import { z } from "zod";
import { RegisterSettingsPatchSchema, RegisterSettingsSchema, Timestamp, Uuid } from "./events";
import { CurrencyCodeSchema, MinorAmount } from "./money";
import {
  CUSTOMER_ADDRESS_MAX_GRAPHEMES,
  CUSTOMER_EMAIL_MAX_CHARS,
  CUSTOMER_NAME_MAX_GRAPHEMES,
  CUSTOMER_PHONE_MAX_CHARS,
  REGISTER_NAME_MAX_GRAPHEMES,
  STANDING_ORDER_CADENCE_MAX_GRAPHEMES,
  graphemeLength,
} from "./text";

/** REST contract between client (C1, C3) and Worker (S2). Error envelope is uniform. */
export const ApiErrorSchema = z.object({
  error: z.object({ code: z.string(), message: z.string(), details: z.unknown().optional() }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export const RoleSchema = z.enum(["owner", "cashier"]);
export type Role = z.infer<typeof RoleSchema>;
/**
 * D-071: the plan an account is on. Both plans are paid; there is no free tier. `none` means no plan: a new
 * account that has not started its free trial, a trial that ended without converting, or a subscription
 * that lapsed. A `none` owner's registers are frozen read-only (D-073); a cashier's own plan never matters.
 */
export const TierSchema = z.enum(["none", "lite", "pro"]);
export type Tier = z.infer<typeof TierSchema>;
/** D-071: the two plans a subscription can be on. */
export const PaidTierSchema = z.enum(["lite", "pro"]);
export type PaidTier = z.infer<typeof PaidTierSchema>;

/** A user's access to one register. Owners hold an implicit grant on every register of their own account. */
export const RegisterGrantSchema = z.object({
  registerId: Uuid,
  role: RoleSchema,
  /** The account that owns the register (differs from the session's accountId for cashiers). */
  ownerAccountId: Uuid,
  /**
   * D-081: when this cashier's session on the register ends; null for no limit and for owners. Defaulted so
   * a session cached by an older Worker still parses.
   */
  sessionEndsAt: z.number().int().nullable().default(null),
});
export type RegisterGrant = z.infer<typeof RegisterGrantSchema>;

/**
 * What the client learns about itself from GET /api/me. Every human signs in with their own identity (F-101);
 * a "cashier" is a signed-in user who accepted an invite, recorded in register_devices (F-802).
 */
export const SessionInfoSchema = z.object({
  userId: Uuid,
  /** The user's own account (every user has one; Lite accounts get one register). */
  accountId: Uuid,
  /** Browser-install identity, generated client-side, sent as the X-Device-Id header, bound on first use. */
  deviceId: Uuid,
  tier: TierSchema,
  /**
   * D-072: epoch ms when the account's free trial ends, while it is in one; null otherwise. Drives the
   * warning banner in the last 48 hours. Defaulted so a session cached by an older Worker still parses.
   */
  trialEndsAt: z.number().int().nullable().default(null),
  email: z.string().optional(),
  emailVerified: z.boolean(),
  grants: z.array(RegisterGrantSchema),
});
export type SessionInfo = z.infer<typeof SessionInfoSchema>;
export const DEVICE_ID_HEADER = "X-Device-Id";
/** D-044: carries the Cloudflare Turnstile widget token on /auth/sign-up/sign-in/request-password-reset. */
export const TURNSTILE_TOKEN_HEADER = "X-Turnstile-Token";
/**
 * D-052. The Stripe webhook, named here because three modules must agree on it exactly: the route itself,
 * the CSRF guard (Stripe sends no Origin) and the global rate limiter (Stripe retries from few IPs).
 * Deliberately outside /api — see src/worker/api/billing/webhook.ts for why.
 */
export const STRIPE_WEBHOOK_PATH = "/stripe/webhook";

export const CreateRegisterBody = z.object({
  name: z.string().trim().min(1).refine((s) => graphemeLength(s) <= REGISTER_NAME_MAX_GRAPHEMES),
  currency: CurrencyCodeSchema,
});
export const UpdateRegisterBody = RegisterSettingsPatchSchema;

export const RegisterSummarySchema = z.object({
  id: Uuid,
  settings: RegisterSettingsSchema,
  configVersion: z.number().int().min(0),
  createdAt: Timestamp,
  archivedAt: Timestamp.nullable(),
  deviceCount: z.number().int().min(0),
});
export type RegisterSummary = z.infer<typeof RegisterSummarySchema>;

/**
 * D-070: what this device may do on a register, read from D1 rather than the 60 s session cache. A cashier
 * device acts on it: `suspended` (joined, but the owner is not on Pro, D-068) makes it read-only, and `none`
 * (revoked, moved away, archived, or never joined) removes the register and its data from the device.
 * D-081: `ended` is a cashier whose session limit has run out. It is removed like `none`, with its own notice.
 */
export const RegisterAccessSchema = z.object({ access: z.enum(["owner", "cashier", "suspended", "ended", "none"]) });
export type RegisterAccess = z.infer<typeof RegisterAccessSchema>;

/** F-801, D-022: how long the invite code itself can be used to join. Nothing to do with how long a joined device may sell. */
export const InviteExpirySchema = z.enum(["1d", "3d", "7d", "none"]);
export type InviteExpiry = z.infer<typeof InviteExpirySchema>;
const HOUR_MS = 3_600_000;
export const INVITE_EXPIRY_MS: Record<InviteExpiry, number | null> = {
  "1d": 24 * HOUR_MS,
  "3d": 3 * 24 * HOUR_MS,
  "7d": 7 * 24 * HOUR_MS,
  none: null,
};
/**
 * D-081: how long each device that joins through the invite may sell, counted from when it joins (or is
 * approved, D-077). When it runs out the device loses the register; scanning the invite again rejoins it.
 */
export const CashierSessionLimitSchema = z.enum(["1h", "4h", "8h", "none"]);
export type CashierSessionLimit = z.infer<typeof CashierSessionLimitSchema>;
export const CASHIER_SESSION_LIMIT_MS: Record<CashierSessionLimit, number | null> = {
  "1h": HOUR_MS,
  "4h": 4 * HOUR_MS,
  "8h": 8 * HOUR_MS,
  none: null,
};
/** D-028: a reusable invite may be joined by at most this many devices. */
export const REUSABLE_INVITE_MAX_USES = 25;

export const CreateInviteBody = z.object({
  expiresIn: InviteExpirySchema,
  reusable: z.boolean().default(false),
  /** D-077: each device that uses the invite waits for the owner to approve it. */
  requiresApproval: z.boolean().default(false),
  /** D-081: how long each device that joins may sell. */
  sessionLimit: CashierSessionLimitSchema.default("none"),
});
export const InviteSchema = z.object({
  id: Uuid,
  registerId: Uuid,
  expiresAt: Timestamp.nullable(),
  maxUses: z.number().int().min(1),
  uses: z.number().int().min(0),
  createdAt: Timestamp,
  revokedAt: Timestamp.nullable(),
  /** D-077. Defaulted so an Invite cached from an older Worker still parses. */
  requiresApproval: z.boolean().default(false),
  /** D-081: each joined device's session length in ms; null for no limit. */
  sessionLimitMs: z.number().int().positive().nullable().default(null),
  /** Full join URL; returned only in the create response, never listed again (S-014). */
  url: z.string().optional(),
});
export type Invite = z.infer<typeof InviteSchema>;

export const AcceptInviteBody = z.object({
  token: z.string().min(16).max(128),
  deviceName: z.string().trim().min(1).max(40),
});
/**
 * POST /api/invites/accept answers a Device (201 joined, 200 already joined), or for an invite that needs
 * approval (D-077) `{ joinRequest }` with 202 while it waits, or the recent denial.
 */
export type AcceptInviteResponse = Device | { joinRequest: JoinRequestStatus };

export const DeviceSchema = z.object({
  id: Uuid,
  registerId: Uuid,
  name: z.string(),
  role: RoleSchema,
  joinedAt: Timestamp,
  lastSeenAt: Timestamp.nullable(),
  revokedAt: Timestamp.nullable(),
  /** D-081: when this device's cashier session ends; null for no limit. */
  sessionEndsAt: Timestamp.nullable().default(null),
});
export type Device = z.infer<typeof DeviceSchema>;
export const MoveDeviceBody = z.object({ registerId: Uuid });

/** D-077: how long a join request waits for the owner before it expires. */
export const JOIN_REQUEST_TTL_MS = 10 * 60_000;
/** D-077: at most this many requests wait on one register at once, so a leaked QR cannot flood the owner. */
export const JOIN_REQUESTS_PENDING_MAX = 5;

/**
 * D-077: a device waiting for the owner's approval, as the owner sees it: in the popup (pushed by the
 * Register DO) and in Setup → Sharing. `requesterEmail` is the requester's sign-in email ("" if none).
 */
export const JoinRequestSchema = z.object({
  id: Uuid,
  registerId: Uuid,
  registerName: z.string(),
  deviceName: z.string(),
  requesterEmail: z.string(),
  createdAt: Timestamp,
  expiresAt: Timestamp,
});
export type JoinRequest = z.infer<typeof JoinRequestSchema>;

export const JoinRequestStatusValueSchema = z.enum(["pending", "approved", "denied", "cancelled", "expired"]);
export type JoinRequestStatusValue = z.infer<typeof JoinRequestStatusValueSchema>;

/** D-077: the requester's view of their own join request, polled while they wait. */
export const JoinRequestStatusSchema = z.object({
  id: Uuid,
  status: JoinRequestStatusValueSchema,
  registerName: z.string(),
  deviceName: z.string(),
  expiresAt: Timestamp,
  /** The new cashier device, once approved. */
  device: DeviceSchema.nullable(),
});
export type JoinRequestStatus = z.infer<typeof JoinRequestStatusSchema>;

export const DirectUploadResponseSchema = z.object({ uploadURL: z.url(), imageId: z.string().min(1) });
export const UploadQuotaSchema = z.object({ period: z.string(), used: z.number().int(), quota: z.number().int() });

// ---------------------------------------------------------------- customers

/** A directory entry (F-901–905, Pro-only). Account-wide: no registerId, unlike products/registers. */
export const CustomerSchema = z.object({
  id: Uuid,
  firstName: z.string(),
  lastName: z.string(),
  /** `""` when the customer has no phone on file (D-064). Kept a string so installed clients keep parsing. */
  phone: z.string(),
  email: z.string().nullable(),
  address: z.string().nullable(),
  hasStandingOrder: z.boolean(),
  standingOrderCadence: z.string().nullable(),
  createdAt: Timestamp,
  archivedAt: Timestamp.nullable(),
});
export type Customer = z.infer<typeof CustomerSchema>;

/**
 * Running sales volume for one customer, one entry per currency the customer has transacted in (D-020: a
 * multi-register Pro account may span several currencies, and summing raw minor units across currencies
 * would be meaningless). Net of refunds; almost always a single entry.
 */
export const CustomerStatsEntrySchema = z.object({
  currency: CurrencyCodeSchema,
  monthMinor: z.number().int(),
  yearMinor: z.number().int(),
  allTimeMinor: z.number().int(),
});
export type CustomerStatsEntry = z.infer<typeof CustomerStatsEntrySchema>;

/** One row of a customer's recent activity (History-style), for CustomerDetail. */
export const CustomerTransactionSummarySchema = z.object({
  id: Uuid,
  registerId: Uuid,
  kind: z.enum(["sale", "refund"]),
  occurredAt: Timestamp,
  totalMinor: MinorAmount,
  currency: CurrencyCodeSchema,
  status: z.string(),
});
export type CustomerTransactionSummary = z.infer<typeof CustomerTransactionSummarySchema>;

export const CustomerDetailSchema = z.object({
  customer: CustomerSchema,
  stats: z.array(CustomerStatsEntrySchema),
  recentTransactions: z.array(CustomerTransactionSummarySchema),
});
export type CustomerDetail = z.infer<typeof CustomerDetailSchema>;

/** Grapheme-bounded free text with a generous hard cap on raw length, so a segmenter never runs on a huge string. */
const customerBoundedText = (max: number) =>
  z.string().trim().max(max * 8).refine((s) => graphemeLength(s) <= max);

/** Limits live in ./text so the client forms share them (D-049); every string here is bounded (S-009). */
export const CreateCustomerBody = z.object({
  firstName: z.string().trim().min(1).refine((s) => graphemeLength(s) <= CUSTOMER_NAME_MAX_GRAPHEMES),
  lastName: z.string().trim().min(1).refine((s) => graphemeLength(s) <= CUSTOMER_NAME_MAX_GRAPHEMES),
  /** Optional since D-064; when sent it must be non-empty. Omitted or null stores no phone. */
  phone: z.string().trim().min(1).max(CUSTOMER_PHONE_MAX_CHARS).nullable().optional(),
  email: z.email().trim().max(CUSTOMER_EMAIL_MAX_CHARS).nullable().optional(),
  address: customerBoundedText(CUSTOMER_ADDRESS_MAX_GRAPHEMES).nullable().optional(),
  hasStandingOrder: z.boolean().default(false),
  standingOrderCadence: customerBoundedText(STANDING_ORDER_CADENCE_MAX_GRAPHEMES).nullable().optional(),
});

/**
 * A PATCH body must mean "change exactly these fields" (D-039/D-045). `.partial()` alone would not strip
 * `hasStandingOrder`'s `.default(false)`, so a patch that only touches (say) the phone number would silently
 * re-materialise `hasStandingOrder: false` and reset it. The field is rebuilt from its unwrapped schema, same
 * fix as `RegisterSettingsPatchSchema`. Keys the caller omits stay absent (verified against Zod's actual
 * partial-parse behavior: an omitted optional-without-default key is not present as an own property at all,
 * so callers/repo code can use `"key" in patch` to tell "omitted" from "explicitly set to null").
 */
export const UpdateCustomerBody = z
  .object({
    firstName: CreateCustomerBody.shape.firstName,
    lastName: CreateCustomerBody.shape.lastName,
    phone: CreateCustomerBody.shape.phone,
    email: CreateCustomerBody.shape.email,
    address: CreateCustomerBody.shape.address,
    hasStandingOrder: CreateCustomerBody.shape.hasStandingOrder.unwrap(),
    standingOrderCadence: CreateCustomerBody.shape.standingOrderCadence,
  })
  .partial();

/** D-052, D-071: the two billing intervals. Rendered as SegmentedButtons, never a select (U-003). */
export const BillingIntervalSchema = z.enum(["monthly", "annual"]);
export type BillingInterval = z.infer<typeof BillingIntervalSchema>;

/** D-071: which plan, billed how often. The 7-day trial is added by the server when the account qualifies. */
export const StartCheckoutBody = z.object({ tier: PaidTierSchema, interval: BillingIntervalSchema });

/** Both /api/billing/checkout and /api/billing/portal answer with a Stripe-hosted URL to send the user to. */
export const BillingRedirectSchema = z.object({ url: z.string() });
export type BillingRedirect = z.infer<typeof BillingRedirectSchema>;

/**
 * What the Setup → Plan screen needs. `status` is Stripe's subscription status passed through verbatim for
 * display; entitlement is always `tier`, never this field. The two differ on purpose during the 14-day
 * past_due grace window, where status is "past_due" but the plan's tier still applies (D-052).
 */
export const BillingSummarySchema = z.object({
  tier: TierSchema,
  /** Null when the account has never subscribed. */
  status: z.string().nullable(),
  /** The plan the subscription is on, whatever its status. Null when there is none or its price is unknown. */
  planTier: PaidTierSchema.nullable(),
  interval: BillingIntervalSchema.nullable(),
  /** Epoch ms. Set only while the subscription is in its free trial: when the first charge is due (D-072). */
  trialEnd: z.number().nullable(),
  /** D-072: the account has never subscribed, so a checkout starts with the 7-day free trial. */
  trialAvailable: z.boolean(),
  /** Epoch ms. When the current paid period ends, i.e. the next renewal date. */
  currentPeriodEnd: z.number().nullable(),
  /** Epoch ms. Set when the customer has cancelled but keeps access until period end. */
  cancelAt: z.number().nullable(),
  /** Epoch ms. Set only while in the past_due grace window: when the plan's access will be revoked. */
  graceDeadline: z.number().nullable(),
  /**
   * D-074: epoch ms. Set only for an account with no plan that has registers: the day its cloud copy becomes
   * due for deletion (90 days after the plan ended, plus the 7-day tombstone).
   */
  deleteAfter: z.number().nullable(),
  /** False when Stripe is not configured on this deployment, or the account has no Stripe customer yet. */
  manageable: z.boolean(),
});
export type BillingSummary = z.infer<typeof BillingSummarySchema>;

/**
 * Route table. `auth`: none | session | owner. Owner routes also require the register to belong to the session's account.
 * Implemented by S2 under src/worker/api; auth routes by S1 under src/worker/auth; /r/* by S3.
 */
export const API_ROUTES = [
  { method: "GET",    path: "/api/me",                                   auth: "session", desc: "SessionInfo for the current session" },
  { method: "GET",    path: "/api/registers",                            auth: "session", desc: "RegisterSummary[] visible to this session" },
  { method: "GET",    path: "/api/registers/:id/access",                 auth: "session", desc: "RegisterAccess for this session's device, fresh from D1 (D-070)" },
  { method: "POST",   path: "/api/registers",                            auth: "owner",   desc: "CreateRegisterBody → RegisterSummary (Free: max 1)" },
  { method: "PATCH",  path: "/api/registers/:id",                        auth: "owner",   desc: "UpdateRegisterBody → RegisterSummary (also emits register.updated via DO)" },
  { method: "DELETE", path: "/api/registers/:id",                        auth: "owner",   desc: "Archive a register" },
  { method: "GET",    path: "/api/registers/:id/invites",                auth: "owner",   desc: "Invite[]" },
  { method: "POST",   path: "/api/registers/:id/invites",                auth: "owner",   desc: "CreateInviteBody → Invite (with url)" },
  { method: "DELETE", path: "/api/invites/:inviteId",                    auth: "owner",   desc: "Revoke invite" },
  { method: "POST",   path: "/api/invites/accept",                       auth: "session", desc: "AcceptInviteBody → Device; binds this session to the register as cashier. An approval invite answers 202 { joinRequest } instead (D-077)" },
  { method: "GET",    path: "/api/join-requests",                        auth: "owner",   desc: "JoinRequest[] waiting on the account's registers (D-077)" },
  { method: "POST",   path: "/api/join-requests/:id/approve",            auth: "owner",   desc: "→ Device, 201; uses one of the invite's uses (Pro, D-077)" },
  { method: "POST",   path: "/api/join-requests/:id/deny",               auth: "owner",   desc: "→ { id, status: 'denied' } (D-077)" },
  { method: "GET",    path: "/api/join-requests/:id/status",             auth: "session", desc: "JoinRequestStatus of the requester's own request, for this user and device only (D-077)" },
  { method: "DELETE", path: "/api/join-requests/:id",                    auth: "session", desc: "The requester withdraws their own pending request (D-077)" },
  { method: "GET",    path: "/api/devices",                              auth: "owner",   desc: "Device[] across all registers on the account" },
  { method: "DELETE", path: "/api/devices/:deviceId",                    auth: "owner",   desc: "Revoke device" },
  { method: "POST",   path: "/api/devices/:deviceId/move",               auth: "owner",   desc: "MoveDeviceBody" },
  { method: "POST",   path: "/api/registers/:id/products/:pid/photo",    auth: "owner",   desc: "→ DirectUploadResponse (Pro; checks quota)" },
  { method: "GET",    path: "/api/quota/photos",                         auth: "owner",   desc: "UploadQuota" },
  { method: "GET",    path: "/api/registers/:id/export",                 auth: "owner",   desc: "BackupFile JSON of cloud-retained events" },
  { method: "GET",    path: "/api/customers",                            auth: "session", desc: "Customer[] on the account (Pro; ?includeArchived=bool; ?registerId= scopes to that register's owner account, so a cashier reads the owner's directory)" },
  { method: "POST",   path: "/api/customers",                            auth: "session", desc: "CreateCustomerBody → Customer, 201 (Pro; ?registerId= as for GET)" },
  { method: "GET",    path: "/api/customers/:id",                        auth: "owner",   desc: "CustomerDetail: record + per-currency stats + recent transactions (Pro)" },
  { method: "PATCH",  path: "/api/customers/:id",                        auth: "owner",   desc: "UpdateCustomerBody → Customer (Pro)" },
  { method: "DELETE", path: "/api/customers/:id",                        auth: "owner",   desc: "Soft delete → { id, archivedAt } (Pro)" },
  { method: "GET",    path: "/api/billing",                             auth: "owner",   desc: "BillingSummary for the account (D-052)" },
  { method: "POST",   path: "/api/billing/checkout",                     auth: "owner",   desc: "StartCheckoutBody → BillingRedirect (Stripe-hosted Checkout)" },
  { method: "POST",   path: "/api/billing/portal",                       auth: "owner",   desc: "→ BillingRedirect (Stripe Customer Portal)" },
  { method: "POST",   path: "/api/billing/restore",                      auth: "owner",   desc: "→ BillingSummary after re-reading the subscription from Stripe; 404 no_subscription (D-062)" },
  { method: "POST",   path: "/stripe/webhook",                           auth: "none",    desc: "Signed Stripe webhook → sets accounts.tier. Outside /api: no session, no Origin, raw body (D-052)" },
  { method: "GET",    path: "/r/:registerId/ws",                         auth: "session", desc: "WebSocket to Register DO (protocol.ts)" },
  { method: "POST",   path: "/r/:registerId/events",                     auth: "session", desc: "PostEventsBody → PostEventsResponse (HTTP fallback)" },
  { method: "GET",    path: "/r/:registerId/events?since=N",             auth: "session", desc: "ServerEvent[] after N" },
  { method: "GET",    path: "/receipt/:token",                           auth: "none",    desc: "Public receipt page (Pro, M4)" },
] as const;
